import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, FileText, TrendingUp, Lightbulb, Shield,
  Trash2, Edit3, Save, X, Plus, BarChart3, BookOpen, Star,
  CheckCircle, XCircle, Clock, Tag, Upload, Video
} from 'lucide-react';
import AppShell from '../components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [vaultItems, setVaultItems] = useState([]);
  const [applications, setApplications] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [editingVault, setEditingVault] = useState(null);
  const [appFilter, setAppFilter] = useState('all');
  const [showAddVault, setShowAddVault] = useState(false);
  const [vaultForm, setVaultForm] = useState({ title: '', description: '', content_type: 'article', featured: false });
  const [videoFile, setVideoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showAddDiscount, setShowAddDiscount] = useState(false);
  const [discountForm, setDiscountForm] = useState({ code: '', discount_percent: 10, description: '', active: true });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [s, m, v, a, d] = await Promise.all([
        axios.get(`${API}/admin/stats`), axios.get(`${API}/admin/members`),
        axios.get(`${API}/admin/vault`), axios.get(`${API}/admin/applications`),
        axios.get(`${API}/admin/discount-codes`)
      ]);
      setStats(s.data); setMembers(m.data); setVaultItems(v.data);
      setApplications(a.data); setDiscountCodes(d.data);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user and all their data?')) return;
    try { await axios.delete(`${API}/admin/users/${userId}`); toast.success('User deleted'); loadAll(); } catch(e) { toast.error('Failed'); }
  };
  const handleRoleChange = async (userId, role) => {
    try { await axios.put(`${API}/admin/users/${userId}/role`, { role }); toast.success(`Role: ${role}`); loadAll(); } catch(e) { toast.error('Failed'); }
  };
  const handleApprove = async (appId) => {
    try { await axios.put(`${API}/admin/applications/${appId}/approve`); toast.success('Application approved! User account created.'); loadAll(); } catch(e) { toast.error('Failed'); }
  };
  const handleReject = async (appId) => {
    if (!window.confirm('Reject this application?')) return;
    try { await axios.put(`${API}/admin/applications/${appId}/reject`); toast.success('Application rejected'); loadAll(); } catch(e) { toast.error('Failed'); }
  };

  const handleAddVault = async () => {
    if (!vaultForm.title.trim()) return;
    setUploading(true);
    try {
      let mediaUrl = '';
      if (videoFile) {
        const fd = new FormData();
        fd.append('file', videoFile);
        const up = await axios.post(`${API}/upload/video`, fd);
        mediaUrl = up.data.url;
      }
      await axios.post(`${API}/vault/content`, { ...vaultForm, media_url: mediaUrl });
      toast.success('Content added!');
      setShowAddVault(false); setVaultForm({ title: '', description: '', content_type: 'article', featured: false }); setVideoFile(null);
      loadAll();
    } catch(e) { toast.error('Failed'); }
    setUploading(false);
  };

  const handleUpdateVault = async (contentId) => {
    try { await axios.put(`${API}/admin/vault/${contentId}`, editingVault); setEditingVault(null); toast.success('Updated'); loadAll(); } catch(e) { toast.error('Failed'); }
  };
  const handleDeleteVault = async (contentId) => {
    if (!window.confirm('Delete?')) return;
    try { await axios.delete(`${API}/admin/vault/${contentId}`); toast.success('Deleted'); loadAll(); } catch(e) { toast.error('Failed'); }
  };

  const handleAddDiscount = async () => {
    if (!discountForm.code.trim()) return;
    try {
      await axios.post(`${API}/admin/discount-codes`, discountForm);
      toast.success('Discount code created!');
      setShowAddDiscount(false); setDiscountForm({ code: '', discount_percent: 10, description: '', active: true });
      loadAll();
    } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleToggleDiscount = async (codeId, active) => {
    try { await axios.put(`${API}/admin/discount-codes/${codeId}`, { active: !active }); loadAll(); } catch(e) { toast.error('Failed'); }
  };
  const handleDeleteDiscount = async (codeId) => {
    if (!window.confirm('Delete this code?')) return;
    try { await axios.delete(`${API}/admin/discount-codes/${codeId}`); toast.success('Deleted'); loadAll(); } catch(e) { toast.error('Failed'); }
  };

  const filteredApps = appFilter === 'all' ? applications : applications.filter(a => a.status === appFilter);
  const pendingCount = applications.filter(a => a.status === 'pending').length;

  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'applications', label: `Applications${pendingCount ? ` (${pendingCount})` : ''}` },
    { key: 'members', label: 'Members' },
    { key: 'vault', label: 'Vault' },
    { key: 'discounts', label: 'Discounts' },
  ];

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-cormorant font-bold text-white mb-1" data-testid="admin-title">Admin Dashboard</h1>
            <p className="text-[#A39BB0] font-inter text-sm">Manage your network</p>
          </div>
          <span className="bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-inter px-3 py-1 rounded-full flex items-center space-x-1 border border-[#D4AF37]/30">
            <Shield className="w-3 h-3" /><span>Admin</span>
          </span>
        </div>

        <div className="flex space-x-1 mb-6 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-full text-sm font-inter font-medium whitespace-nowrap transition-all ${
                tab === t.key ? 'bg-white/10 text-white border border-white/10' : 'text-[#A39BB0] hover:text-white hover:bg-white/5'
              }`} data-testid={`admin-tab-${t.key}`}>{t.label}</button>
          ))}
        </div>

        {loading ? <div className="text-center text-[#A39BB0] py-16">Loading...</div> :

        /* ===== OVERVIEW ===== */
        tab === 'overview' ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats && [
                { label: 'Members', val: stats.total_users, icon: Users, color: 'purple' },
                { label: 'Applications', val: stats.total_applications, icon: FileText, color: 'pink' },
                { label: 'Pending', val: stats.pending_applications, icon: Clock, color: 'amber' },
                { label: 'Posts', val: stats.total_posts, icon: TrendingUp, color: 'emerald' },
                { label: 'Vault', val: stats.vault_items, icon: BookOpen, color: 'blue' },
                { label: 'Ideas', val: stats.total_ideas, icon: Lightbulb, color: 'orange' },
              ].map(s => (
                <div key={s.label} className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-5">
                  <s.icon className={`w-5 h-5 text-${s.color}-400 mb-2`} />
                  <p className="text-3xl font-cormorant font-bold text-white">{s.val}</p>
                  <p className="text-[#A39BB0] text-xs font-inter">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        ) :

        /* ===== APPLICATIONS ===== */
        tab === 'applications' ? (
          <div className="space-y-4">
            <div className="flex space-x-2 mb-4">
              {['all', 'pending', 'approved', 'rejected'].map(f => (
                <button key={f} onClick={() => setAppFilter(f)}
                  className={`px-3 py-1.5 rounded-full text-xs font-inter font-medium capitalize ${appFilter === f ? 'bg-white/10 text-white' : 'text-[#A39BB0] hover:bg-white/5'}`}
                  data-testid={`app-filter-${f}`}>{f}</button>
              ))}
            </div>
            {filteredApps.length === 0 ? (
              <div className="text-center py-12 text-[#A39BB0] font-inter text-sm">No applications</div>
            ) : filteredApps.map(app => (
              <div key={app.application_id} className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-5" data-testid={`app-${app.application_id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-white font-inter font-semibold text-sm">{app.full_name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-inter font-bold uppercase ${
                        app.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                        app.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                      }`}>{app.status}</span>
                      <span className="bg-purple-500/15 text-purple-300 px-2 py-0.5 rounded-full text-[10px] font-inter">{app.selected_tier}</span>
                    </div>
                    <p className="text-[#A39BB0] text-xs font-inter mb-1">{app.email} · {app.current_role} · {app.industry}</p>
                    {app.goals && <p className="text-white/60 text-xs font-inter mb-1"><span className="text-[#A39BB0]">Goals:</span> {app.goals}</p>}
                    {app.why_pivot && <p className="text-white/60 text-xs font-inter"><span className="text-[#A39BB0]">Why P2P:</span> {app.why_pivot}</p>}
                    <p className="text-[#A39BB0]/50 text-[10px] font-inter mt-2">{new Date(app.submitted_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                  </div>
                  {app.status === 'pending' && (
                    <div className="flex items-center space-x-2 ml-4">
                      <button onClick={() => handleApprove(app.application_id)}
                        className="flex items-center space-x-1 bg-emerald-500/20 text-emerald-400 px-3 py-1.5 rounded-full text-xs font-inter font-bold hover:bg-emerald-500/30"
                        data-testid={`approve-${app.application_id}`}>
                        <CheckCircle className="w-3.5 h-3.5" /><span>Approve</span>
                      </button>
                      <button onClick={() => handleReject(app.application_id)}
                        className="flex items-center space-x-1 bg-red-500/20 text-red-400 px-3 py-1.5 rounded-full text-xs font-inter font-bold hover:bg-red-500/30"
                        data-testid={`reject-${app.application_id}`}>
                        <XCircle className="w-3.5 h-3.5" /><span>Reject</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) :

        /* ===== MEMBERS ===== */
        tab === 'members' ? (
          <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] overflow-hidden">
            <div className="px-6 py-4 border-b border-white/[0.06]">
              <h2 className="text-lg font-cormorant font-bold text-white">All Members ({members.length})</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="border-b border-white/[0.06]">
                  {['Name', 'Email', 'Role', 'Status', 'Actions'].map(h => (
                    <th key={h} className="text-left py-3 px-5 text-[#A39BB0] text-xs font-inter font-bold uppercase tracking-wider">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {members.map(m => (
                    <tr key={m.user_id} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`admin-member-${m.user_id}`}>
                      <td className="py-3 px-5">
                        <div className="flex items-center space-x-2">
                          {m.photo_url ? <img src={m.photo_url} alt="" className="w-7 h-7 rounded-full object-cover" /> :
                           <div className="w-7 h-7 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center"><span className="text-white text-[10px] font-bold">{m.full_name?.charAt(0)}</span></div>}
                          <span className="text-white font-inter text-sm">{m.full_name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-5 text-[#A39BB0] font-inter text-sm">{m.email}</td>
                      <td className="py-3 px-5">
                        <select value={m.role || 'member'} onChange={e => handleRoleChange(m.user_id, e.target.value)}
                          className="bg-white/[0.05] border border-white/[0.1] rounded-lg px-2 py-1 text-xs font-inter text-white outline-none"
                          data-testid={`role-select-${m.user_id}`}>
                          <option value="member" className="bg-gray-900">Member</option>
                          <option value="admin" className="bg-gray-900">Admin</option>
                        </select>
                      </td>
                      <td className="py-3 px-5"><span className={`w-2 h-2 rounded-full inline-block mr-1 ${m.onboarding_completed ? 'bg-emerald-400' : 'bg-amber-400'}`} /><span className="text-[#A39BB0] text-xs font-inter">{m.onboarding_completed ? 'Active' : 'Pending'}</span></td>
                      <td className="py-3 px-5"><button onClick={() => handleDeleteUser(m.user_id)} className="text-[#A39BB0] hover:text-red-400"><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) :

        /* ===== VAULT ===== */
        tab === 'vault' ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-cormorant font-bold text-white">Power Vault ({vaultItems.length})</h2>
              <button onClick={() => setShowAddVault(!showAddVault)}
                className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold text-sm px-4 py-2 rounded-full"
                data-testid="add-vault-button"><Plus className="w-4 h-4" /><span>Add Content</span></button>
            </div>

            {showAddVault && (
              <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-6" data-testid="vault-add-form">
                <h3 className="text-white font-inter font-bold text-sm mb-4">New Content</h3>
                <div className="space-y-3">
                  <input value={vaultForm.title} onChange={e => setVaultForm({...vaultForm, title: e.target.value})}
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-2.5 text-white text-sm font-inter outline-none"
                    placeholder="Title" data-testid="vault-title-input" />
                  <textarea value={vaultForm.description} onChange={e => setVaultForm({...vaultForm, description: e.target.value})}
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-2.5 text-white text-sm font-inter outline-none min-h-[80px] resize-none"
                    placeholder="Description" data-testid="vault-description-input" />
                  <div className="flex flex-wrap items-center gap-3">
                    <select value={vaultForm.content_type} onChange={e => setVaultForm({...vaultForm, content_type: e.target.value})}
                      className="bg-white/[0.05] border border-white/[0.1] rounded-xl px-3 py-2.5 text-sm font-inter text-white outline-none"
                      data-testid="vault-type-select">
                      <option value="article" className="bg-gray-900">Article</option>
                      <option value="interview" className="bg-gray-900">Interview</option>
                      <option value="spotlight" className="bg-gray-900">Spotlight</option>
                      <option value="video" className="bg-gray-900">Video</option>
                    </select>
                    <label className="flex items-center space-x-2 text-[#A39BB0] text-sm font-inter cursor-pointer">
                      <input type="checkbox" checked={vaultForm.featured} onChange={e => setVaultForm({...vaultForm, featured: e.target.checked})} className="accent-purple-500" />
                      <span>Featured</span>
                    </label>
                  </div>
                  {vaultForm.content_type === 'video' && (
                    <div className="border-2 border-dashed border-white/[0.1] rounded-xl p-5 text-center">
                      {videoFile ? (
                        <div className="flex items-center justify-center space-x-2">
                          <Video className="w-5 h-5 text-purple-400" />
                          <span className="text-white font-inter text-sm">{videoFile.name}</span>
                          <button onClick={() => setVideoFile(null)} className="text-[#A39BB0] hover:text-red-400"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <Upload className="w-8 h-8 text-white/20 mx-auto mb-2" />
                          <p className="text-[#A39BB0] text-sm font-inter">Click to upload video (MP4, WebM, MOV)</p>
                          <input type="file" accept="video/*" className="hidden" onChange={e => setVideoFile(e.target.files[0])} data-testid="vault-video-input" />
                        </label>
                      )}
                    </div>
                  )}
                  <div className="flex justify-end space-x-2 pt-2">
                    <button onClick={() => setShowAddVault(false)} className="text-[#A39BB0] text-sm font-inter px-4 py-2">Cancel</button>
                    <button onClick={handleAddVault} disabled={!vaultForm.title.trim() || uploading}
                      className="bg-purple-600 text-white font-inter font-bold text-sm px-5 py-2 rounded-full disabled:opacity-50"
                      data-testid="vault-save-button">{uploading ? 'Uploading...' : 'Publish'}</button>
                  </div>
                </div>
              </div>
            )}

            {vaultItems.map(item => (
              <div key={item.content_id} className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-5" data-testid={`vault-item-${item.content_id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-white font-inter font-medium text-sm">{item.title}</h3>
                      {item.featured && <span className="bg-amber-500/20 text-amber-400 text-[10px] font-inter font-bold px-2 py-0.5 rounded-full"><Star className="w-2.5 h-2.5 inline" /> Featured</span>}
                      <span className="bg-purple-500/15 text-purple-300 text-[10px] font-inter px-2 py-0.5 rounded-full uppercase">{item.content_type}</span>
                      {item.media_url && <span className="bg-emerald-500/15 text-emerald-300 text-[10px] font-inter px-2 py-0.5 rounded-full">Has Video</span>}
                    </div>
                    <p className="text-[#A39BB0] text-xs font-inter line-clamp-1">{item.description}</p>
                  </div>
                  <div className="flex items-center space-x-1 ml-4">
                    <button onClick={() => setEditingVault({ content_id: item.content_id, title: item.title, description: item.description })}
                      className="text-[#A39BB0] hover:text-purple-400 p-1"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => handleDeleteVault(item.content_id)}
                      className="text-[#A39BB0] hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) :

        /* ===== DISCOUNTS ===== */
        tab === 'discounts' ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-cormorant font-bold text-white">Discount Codes ({discountCodes.length})</h2>
              <button onClick={() => setShowAddDiscount(!showAddDiscount)}
                className="flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-pink-500 text-black font-inter font-bold text-sm px-4 py-2 rounded-full"
                data-testid="add-discount-button"><Plus className="w-4 h-4" /><span>New Code</span></button>
            </div>

            {showAddDiscount && (
              <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-6" data-testid="discount-add-form">
                <h3 className="text-white font-inter font-bold text-sm mb-4">Create Discount Code</h3>
                <div className="space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[#A39BB0] text-xs font-inter mb-1">Code</label>
                      <input value={discountForm.code} onChange={e => setDiscountForm({...discountForm, code: e.target.value.toUpperCase()})}
                        className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-2.5 text-white text-sm font-inter outline-none uppercase"
                        placeholder="FOUNDING50" data-testid="discount-code-input" />
                    </div>
                    <div>
                      <label className="block text-[#A39BB0] text-xs font-inter mb-1">Discount %</label>
                      <input type="number" min="1" max="100" value={discountForm.discount_percent}
                        onChange={e => setDiscountForm({...discountForm, discount_percent: parseInt(e.target.value) || 0})}
                        className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-2.5 text-white text-sm font-inter outline-none"
                        data-testid="discount-percent-input" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[#A39BB0] text-xs font-inter mb-1">Description</label>
                    <input value={discountForm.description} onChange={e => setDiscountForm({...discountForm, description: e.target.value})}
                      className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-2.5 text-white text-sm font-inter outline-none"
                      placeholder="Founding Member - 50% Off" data-testid="discount-desc-input" />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button onClick={() => setShowAddDiscount(false)} className="text-[#A39BB0] text-sm font-inter px-4 py-2">Cancel</button>
                    <button onClick={handleAddDiscount} disabled={!discountForm.code.trim()}
                      className="bg-amber-500 text-black font-inter font-bold text-sm px-5 py-2 rounded-full disabled:opacity-50"
                      data-testid="discount-save-button">Create Code</button>
                  </div>
                </div>
              </div>
            )}

            {discountCodes.length === 0 ? (
              <div className="text-center py-12 text-[#A39BB0] font-inter text-sm">No discount codes yet</div>
            ) : discountCodes.map(code => (
              <div key={code.code_id || code.code} className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-5 flex items-center justify-between"
                data-testid={`discount-${code.code}`}>
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
                    <Tag className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-white font-inter font-bold text-sm font-mono">{code.code}</span>
                      <span className="bg-pink-500/20 text-pink-400 text-[10px] font-inter font-bold px-2 py-0.5 rounded-full">{code.discount_percent}% OFF</span>
                      <span className={`text-[10px] font-inter font-bold px-2 py-0.5 rounded-full ${code.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                        {code.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {code.description && <p className="text-[#A39BB0] text-xs font-inter mt-0.5">{code.description}</p>}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button onClick={() => handleToggleDiscount(code.code_id, code.active)}
                    className="text-[#A39BB0] hover:text-amber-400 text-xs font-inter px-3 py-1 bg-white/[0.05] rounded-full border border-white/[0.08]">
                    {code.active ? 'Disable' : 'Enable'}
                  </button>
                  <button onClick={() => handleDeleteDiscount(code.code_id)}
                    className="text-[#A39BB0] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </AppShell>
  );
}
